import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { camelizeKeys } from "utils/camelizeKeys";
import { formatL0InfoLabel } from "utils/formatL0InfoDisplay";

import { Dialog, PrimaryButton, SecondaryButton, Stepper } from "widgets";
import PackageSubscription from "components/PackageSubscription/PackageSubscription";
import OrderSummary from "components/OrderSummary/OrderSummary";
import ConfirmOrder from "components/ConfirmOrder/ConfirmOrder";
import {
  useFetchPackagesAndSubscriptions,
  useFetchUser,
  usePunchOrder,
  useSelectedOrderDetails,
} from "store/backend/assets";
import {
  BUSINESS_VERTICALS,
  DISCOUNT_TYPE,
  KH_VERTICAL_ID,
  NO_OF_PACKAGE_SUBSCRIPTION_IN_KH,
  ZERO,
} from "constants/config";
import ReviewTransferOrder from "components/ReviewTransferOrder/ReviewTransferOrder";
import RequestTransferOrder from "components/RequestTransferOrder/RequestTransferOrder";
import { validateOrderAddresses } from "utils/addressValidation";
import { buildAddressPayload } from "utils/addressPayload";
import {
  getOrderShippingAddress,
  mapCapturedAddressToForm,
  orderHadShippingAddress,
  resolveRequiresShippingAddress,
} from "utils/orderShipping";
import {
  useCountryIsdList,
  useCurrencyByCountryMap,
  useCurrencyList,
  findCountryInIsdList,
} from "services/mdsService";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { resolveCurrencyCodeByCountryId } from "utils/formatMonetaryAmount";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import PageNotFound from "components/PageNotFound/PageNotFound";
import dayjs from "dayjs";
import { parseDateWithFormat } from "utils/parseDateWithFormat";
import {
  buildBatchPreferenceUrl,
  buildPaymentPreferenceUrl,
} from "utils/customerFlowUrls";
import { grantBatchPreferenceAccess } from "utils/batchPreferenceStorage";
import { isLearnBusinessUnit } from "utils/batchPreferenceUtils";

import styles from "./PunchOrder.module.scss";

const PACKAGE_SUBSCRIPTION_SELECTION = 0;
const ADDRESS_FORM_AND_DISCOUNT_APPLICATION = 1;
const REVIEW_TRANSFER_ORDER = 2;

const STEPS_LABEL = [
  { label: "Select Packages" },
  { label: "Add billing info and discount" },
];

const buildNameFromNested = (nested) => {
  const fn = nested.firstName ?? nested.first_name;
  const ln = nested.lastName ?? nested.last_name;
  if (fn || ln) {
    return [fn, ln].filter(Boolean).join(" ").trim();
  }
  return (
    nested.name ??
    nested.fullName ??
    nested.full_name ??
    nested.label ??
    ""
  );
};

const getL0FromTransferOrder = (transferOrderDetails) => {
  if (!transferOrderDetails) {
    return { attributedTo: undefined, attributedToName: "", l0Info: null };
  }
  const t = transferOrderDetails;

  const l0InfoRaw = t.l0Info ?? t.l0_info;
  if (l0InfoRaw && typeof l0InfoRaw === "object" && !Array.isArray(l0InfoRaw)) {
    const l0Info = l0InfoRaw;
    const labelFromL0 = formatL0InfoLabel(l0Info);
    const attributedTo =
      l0Info.uamId ??
      l0Info.uam_id ??
      l0Info.id ??
      t.attributedTo ??
      t.attributed_to;
    const attributedToName =
      labelFromL0 ||
      buildNameFromNested(l0Info) ||
      (t.attributedToName ?? t.attributed_to_name ?? "");
    return {
      attributedTo,
      attributedToName: attributedToName || "",
      l0Info,
    };
  }

  const nested =
    t.attributedSalesPerson ||
    t.attributed_sales_person ||
    t.l0SalesPerson ||
    t.l0_sales_person ||
    t.attributedToDetails ||
    null;

  let attributedTo =
    t.attributedTo ??
    t.l0SalesPersonId ??
    t.attributed_to_uam_id ??
    t.attributed_to;

  let attributedToName =
    t.attributedToName ??
    t.l0SalesPersonName ??
    t.attributed_to_name ??
    "";

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    attributedTo =
      attributedTo ??
      nested.uamId ??
      nested.uam_id ??
      nested.id ??
      nested.crnId ??
      nested.crn_id;
    if (!attributedToName) {
      attributedToName = buildNameFromNested(nested);
    }
  }

  return {
    attributedTo,
    attributedToName: attributedToName || "",
    l0Info: null,
  };
};

/** True when the original order payload includes L0 (show read-only); else user must search. */
const isL0ProvidedByOrderData = (l0) => {
  if (!l0) return false;
  if (
    l0.l0Info &&
    typeof l0.l0Info === "object" &&
    !Array.isArray(l0.l0Info) &&
    Object.keys(l0.l0Info).length > 0
  ) {
    return true;
  }
  if (l0.attributedTo != null && l0.attributedTo !== "") {
    return true;
  }
  if (l0.attributedToName && String(l0.attributedToName).trim() !== "") {
    return true;
  }
  return false;
};

const SyncTransferOrderL0Fields = ({
  isOrderTransfer,
  orderInfoForTransfer,
  transferOrderDetails,
  setFieldValue,
}) => {
  useEffect(() => {
    if (!isOrderTransfer || !orderInfoForTransfer || !transferOrderDetails) {
      return;
    }
    const merged = {
      ...transferOrderDetails,
      ...camelizeKeys(orderInfoForTransfer),
    };
    const l0 = getL0FromTransferOrder(merged);
    const hasL0FromOrder = isL0ProvidedByOrderData(l0);
    setFieldValue("orderDetails.l0ProvidedByOrder", hasL0FromOrder);
    if (!hasL0FromOrder) {
      return;
    }
    setFieldValue("orderDetails.attributedTo", l0.attributedTo);
    setFieldValue("orderDetails.attributedToName", l0.attributedToName);
    setFieldValue("orderDetails.l0Info", l0.l0Info ?? null);
  }, [
    isOrderTransfer,
    orderInfoForTransfer,
    transferOrderDetails,
    setFieldValue,
  ]);
  return null;
};

const getInitialValues = (
  transferedPackageSubscriptions,
  transferedOrderVerticalAndTenantIds,
  selectedBusinessVerticalId,
  transferOrderDetails,
  customerCountryId,
) => {
  const l0FromOriginalOrder = getL0FromTransferOrder(transferOrderDetails);
  const previousOrderHadShipping =
    orderHadShippingAddress(transferOrderDetails);
  const hasHardware = resolveRequiresShippingAddress(
    transferedPackageSubscriptions,
    previousOrderHadShipping
  );

  return {
    orderDetails: {
      billingAddress: {},
      shippingAddress: hasHardware
        ? mapCapturedAddressToForm(
            getOrderShippingAddress(transferOrderDetails)
          )
        : undefined,
      discount: {},
      hasHardware,
      serviceStartDate: "",
      attributedTo: l0FromOriginalOrder.attributedTo,
      attributedToName: l0FromOriginalOrder.attributedToName,
      l0Info: l0FromOriginalOrder.l0Info ?? null,
      l0ProvidedByOrder: isL0ProvidedByOrderData(l0FromOriginalOrder),
    },
    priceDetailsAfterDiscount: {},
    selectedPackageSubscriptions: transferedPackageSubscriptions || [],
    currentStep: PACKAGE_SUBSCRIPTION_SELECTION,
    filters: {
      selectedGrade: [],
      selectedTargetExam: [],
      selectedVerticalId:
        transferedOrderVerticalAndTenantIds.verticalId ||
        Number(selectedBusinessVerticalId),
      selectedTenantId: transferedOrderVerticalAndTenantIds.tenantId || null,
      selectedGatewayTenantId:
        transferedOrderVerticalAndTenantIds.gatewayTenantId || null,
      selectedOfficeLocationId:
        transferedOrderVerticalAndTenantIds.officeLocationId || null,
      selectedCountryId: customerCountryId ?? null,
    },
    searchTerm: "",
    useSameAddress: false,
    openDialog: false,
  };
};

const buildSkuPayload = (selectedPackageSubscriptions) =>
  selectedPackageSubscriptions.reduce((acc, selectedPackage) => {
    const existingPackage = acc.find(
      (result) => result.id === selectedPackage.packageId
    );

    if (existingPackage) {
      return acc.map((result) =>
        result.id === selectedPackage.packageId
          ? {
              ...result,
              subscriptions: [
                ...result.subscriptions,
                { id: selectedPackage.id },
              ],
            }
          : result
      );
    }

    return [
      ...acc,
      {
        id: selectedPackage.packageId,
        subscriptions: [{ id: selectedPackage.id }],
      },
    ];
  }, []);

const totalMrp = (selectedPackageSubscriptions) =>
  selectedPackageSubscriptions.reduce((total, packageSubscription) => {
    return total + (packageSubscription.mrp || 0);
  }, 0);

const buildDiscountPayload = (
  discountDetails,
  listedOrderAmount,
  orderAmount,
  priceDetailsAfterDiscount
) => {
  if (!discountDetails?.discount_type) {
    return null;
  }

  if (
    priceDetailsAfterDiscount?.listedOrderAmount == null ||
    priceDetailsAfterDiscount?.orderAmount == null
  ) {
    return null;
  }

  const computedDiscount = Number(listedOrderAmount) - Number(orderAmount);
  const discount = reduceToFixedDecimals(
    Number.isNaN(computedDiscount) ? ZERO : Math.max(ZERO, computedDiscount)
  );

  if (discount <= ZERO) {
    return null;
  }

  if (discountDetails.discount_type === DISCOUNT_TYPE.DYNAMIC) {
    return {
      discount_type: discountDetails.discount_type,
      discount,
    };
  }

  if (discountDetails.discount_type === DISCOUNT_TYPE.COUPON_CODE) {
    if (!discountDetails.coupon_code) {
      return null;
    }
    return {
      discount_type: discountDetails.discount_type,
      coupon_code: discountDetails.coupon_code,
      discount,
    };
  }

  return null;
};

const formatServiceStartDateForV2 = (serviceStartDate) => {
  if (!serviceStartDate) {
    return serviceStartDate;
  }

  const parsed = parseDateWithFormat(serviceStartDate, "DD-MM-YYYY");
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  const isoParsed = dayjs(serviceStartDate);
  return isoParsed.isValid() ? isoParsed.format("YYYY-MM-DD") : serviceStartDate;
};

const buildPayload = (values, newUamId, { isdCode, countryCode } = {}) => {
  const {
    orderDetails,
    selectedPackageSubscriptions,
    priceDetailsAfterDiscount,
    filters,
  } = values;

  const listedOrderAmount =
    priceDetailsAfterDiscount.listedOrderAmount ||
    totalMrp(selectedPackageSubscriptions);

  const orderAmount =
    priceDetailsAfterDiscount.orderAmount ??
    totalMrp(selectedPackageSubscriptions);

  const discountPayload = buildDiscountPayload(
    orderDetails.discount,
    listedOrderAmount,
    orderAmount,
    priceDetailsAfterDiscount
  );

  const discount =
    !!discountPayload && !!discountPayload.discount_type
      ? discountPayload
      : null;

  const addressCountry = {
    countryId: filters.selectedCountryId,
    isdCode,
    countryCode,
  };

  const billing_address = buildAddressPayload(
    orderDetails.billingAddress,
    addressCountry
  );

  const requiresShipping = Boolean(orderDetails.hasHardware);
  const shipping_address = requiresShipping
    ? values.useSameAddress
      ? billing_address
      : buildAddressPayload(orderDetails.shippingAddress, addressCountry)
    : null;

  return {
    gateway_tenant_id: filters.selectedGatewayTenantId,
    amount: reduceToFixedDecimals(orderAmount),
    attributed_to:
      orderDetails.attributedTo != null
        ? String(orderDetails.attributedTo)
        : null,
    customer_id: String(newUamId),
    service_start_date: formatServiceStartDateForV2(
      orderDetails.serviceStartDate
    ),
    skus: buildSkuPayload(selectedPackageSubscriptions),
    address: {
      billing_address,
      shipping_address,
    },
    discount,
  };
};

const advanceToNextStep = (
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
  previousOrderHadShipping = false,
) => {
  setFieldValue("currentStep", currentStep + 1);
  setFieldValue(
    "orderDetails.hasHardware",
    resolveRequiresShippingAddress(
      selectedPackageSubscriptions,
      previousOrderHadShipping
    )
  );
};

const handleClickNextButton = async ({
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
  isOrderTransfer,
  openSnackbar,
  refetchEnrichedSubscriptions,
  previousOrderHadShipping = false,
}) => {
  if (currentStep === PACKAGE_SUBSCRIPTION_SELECTION && isOrderTransfer) {
    const { data, error } = await refetchEnrichedSubscriptions();
    if (error) {
      openSnackbar(
        error?.message ||
          "Failed to fetch package book prices. Please try again.",
        "error",
      );
      return;
    }
    setFieldValue("selectedPackageSubscriptions", data);
    advanceToNextStep(
      currentStep,
      setFieldValue,
      data,
      previousOrderHadShipping
    );
    return;
  }

  advanceToNextStep(
    currentStep,
    setFieldValue,
    selectedPackageSubscriptions,
    previousOrderHadShipping
  );
};

const punchOrderAfterSetUamId = (
  values,
  navigate,
  punchOrder,
  openSnackbar,
  uam_id,
  { isdCode, countryCode, countryId, verticalId }
) => {
  const parsedUamId = uam_id === "null" ? null : uam_id;

  if (!Boolean(parsedUamId)) {
    openSnackbar(
      "An unexpected error occurred while punching the order. Please try again.",
      "error"
    );
  } else {
    const payload = buildPayload(values, uam_id, {
      isdCode,
      countryCode,
    });

    punchOrder(payload, {
      onSuccess: (orderId) => {
        const businessVerticalId =
          verticalId || values.filters?.selectedVerticalId;
        const shouldOpenBatchPreference = isLearnBusinessUnit(businessVerticalId);

        if (!shouldOpenBatchPreference) {
          navigate(
            buildPaymentPreferenceUrl({
              orderId,
              countryId,
            }),
            {
              replace: true,
            },
          );
          return;
        }

        grantBatchPreferenceAccess(orderId);
        navigate(
          buildBatchPreferenceUrl({
            orderId,
            uamId: parsedUamId,
            countryId,
          }),
          {
            replace: true,
          },
        );
      },
      onError: (error) => {
        openSnackbar(
          error?.message ||
            "An unexpected error occurred while processing the order. Please try again.",
          "error"
        );
      },
    });
  }
};

const getPackageSubscriptions = (transferedPackages, verticalId) => {
  if (!transferedPackages || transferedPackages.length === 0) {
    return [];
  }
  return transferedPackages.flatMap((pkg) => {
    if (!pkg.subscriptions || pkg.subscriptions.length === 0) {
      return [];
    }

    return pkg.subscriptions.map((subscription) => {
      return {
        id: subscription.id,
        booksPrice: subscription.bookPrice || 0,
        packageId: pkg.id,
        verticalId: verticalId,
        displayName: pkg.displayName,
        planName: subscription.planCategory.name,
        planValidity: pkg.validityDate,
        mrp: subscription.mrp,
        componentProductTypeIds:
          subscription.components?.map(
            (component) => component.componentProduct?.componentProductTypeId
          ) || [],
        componentCategoryIds:
          subscription.components?.map(
            (component) => component.componentCategory?.componentCategoryId
          ) || [],
      };
    });
  });
};

const getVerticalAndTenantIdsOfTransferedOrder = (transferOrderDetails) => {
  if (!transferOrderDetails) {
    return {};
  }
  return {
    verticalId: transferOrderDetails.businessUnitId,
    tenantId: transferOrderDetails.tenantId,
    gatewayTenantId: transferOrderDetails.gatewayTenantId ?? null,
    officeLocationId: transferOrderDetails.officeLocationId,
  };
};

const handleDialogOpen =
  (setFieldValue, content, size = {}) =>
  () => {
    setFieldValue("openDialog", { content, size });
  };

const isSaveAndContinueButtonDisabled = (
  selectedPackageSubscriptions,
  filters,
  currentStep,
  isValid,
  isOrderTransfer,
  serviceStartDate,
  attributedTo
) => {
  if (selectedPackageSubscriptions.length === 0) return true;

  if (
    filters.selectedVerticalId === BUSINESS_VERTICALS.KH &&
    selectedPackageSubscriptions.length !== NO_OF_PACKAGE_SUBSCRIPTION_IN_KH
  )
    return true;

  if (!filters.selectedVerticalId) return true;
  if (isOrderTransfer && !filters.selectedTenantId) return true;
  const onAddressStep = currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION;

  if (
    onAddressStep &&
    isOrderTransfer &&
    (!isValid || !serviceStartDate || !attributedTo)
  )
    return true;

  if (onAddressStep && !isOrderTransfer && (!isValid || !attributedTo))
    return true;
  if (
    filters.selectedVerticalId === KH_VERTICAL_ID &&
    selectedPackageSubscriptions.length > 1
  )
    return true;
  return false;
};

const PunchOrderStepActions = ({
  currentStep,
  setFieldValue,
  selectedPackageSubscriptions,
  filters,
  isValid,
  isOrderTransfer,
  isSubmitting,
  orderDetails,
  openSnackbar,
  values,
  navigate,
  punchOrder,
  sales_person_id,
  uam_id,
  isPunchOrderLoading,
  error,
  isError,
  transferOrderDetails,
  selectedBusinessVerticalId,
  customerCountryId,
  customerIsdCode,
  customerCountryCode,
  customerCountryName,
  previousOrderHadShipping = false,
}) => {
  const {
    refetch: refetchEnrichedSubscriptions,
    isFetching: isEnrichingBookPrices,
  } = useFetchPackagesAndSubscriptions(selectedPackageSubscriptions, {
    enabled: false,
    countryId: customerCountryId,
  });

  return (
    <div className={styles.lineItemWrapper}>
      {currentStep > PACKAGE_SUBSCRIPTION_SELECTION && (
        <SecondaryButton
          onClick={() => setFieldValue("currentStep", currentStep - 1)}
        >
          Go Back
        </SecondaryButton>
      )}
      {currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION &&
      !isOrderTransfer ? (
        <PrimaryButton
          loading={isSubmitting}
          onClick={handleDialogOpen(
            setFieldValue,
            <ConfirmOrder
              onConfirm={() =>
                punchOrderAfterSetUamId(
                  values,
                  navigate,
                  punchOrder,
                  openSnackbar,
                  uam_id,
                  {
                    isdCode: customerIsdCode,
                    countryCode: customerCountryCode,
                    countryId: customerCountryId,
                    verticalId: selectedBusinessVerticalId,
                  },
                )
              }
              onCancel={() => setFieldValue("openDialog", false)}
              isPending={isPunchOrderLoading}
              error={error}
              isError={isError}
            />,
            { width: "25rem", height: "10rem" },
          )}
          disabled={
            !orderDetails.serviceStartDate ||
            !isValid ||
            !orderDetails.attributedTo ||
            !filters.selectedTenantId ||
            !filters.selectedGatewayTenantId ||
            !customerIsdCode ||
            !customerCountryCode
          }
        >
          Confirm Order
        </PrimaryButton>
      ) : (
        currentStep !== REVIEW_TRANSFER_ORDER && (
          <PrimaryButton
            loading={isSubmitting || isEnrichingBookPrices}
            disabled={
              isEnrichingBookPrices ||
              isSaveAndContinueButtonDisabled(
                selectedPackageSubscriptions,
                filters,
                currentStep,
                isValid,
                isOrderTransfer,
                orderDetails.serviceStartDate,
                orderDetails.attributedTo,
              )
            }
            onClick={() =>
              handleClickNextButton({
                currentStep,
                setFieldValue,
                selectedPackageSubscriptions,
                isOrderTransfer,
                openSnackbar,
                refetchEnrichedSubscriptions,
                previousOrderHadShipping,
              })
            }
          >
            Save & Continue
          </PrimaryButton>
        )
      )}
      {currentStep === REVIEW_TRANSFER_ORDER ? (
        <PrimaryButton
          onClick={handleDialogOpen(
            setFieldValue,
            <RequestTransferOrder
              values={values}
              uam_id={uam_id}
              sales_person_id={sales_person_id}
              orderId={transferOrderDetails.orderId}
              selectedBusinessVerticalId={selectedBusinessVerticalId}
              transferOrderDetails={transferOrderDetails}
              customerCountryId={customerCountryId}
              customerIsdCode={customerIsdCode}
              customerCountryCode={customerCountryCode}
              customerCountryName={customerCountryName}
              onCancel={() => setFieldValue("openDialog", false)}
            />,
            { width: "25rem", height: "10rem" },
          )}
        >
          Confirm Modification
        </PrimaryButton>
      ) : null}
    </div>
  );
};

const getPreviousOrderAcademicYear = (transferOrderDetails) => {
  const { businessUnitId, packages } = transferOrderDetails;

  if (businessUnitId !== KH_VERTICAL_ID) {
    return null;
  }
  if (!Array.isArray(packages) || packages.length === ZERO) {
    return null;
  }
  return packages[ZERO].academicYear ?? null;
};

const PunchOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const uam_id = searchParams.get("uamId");
  const selectedBusinessVerticalId = searchParams.get("verticalId");
  const customerCountryIdParam = searchParams.get("countryId");
  const [sales_person_id] = useState(localStorage.getItem("sales_person_id"));
  const location = useLocation();
  const transferOrderDetails = location.state?.transferOrderDetails
    ? camelizeKeys(location.state.transferOrderDetails)
    : null;

  const isOrderTransfer =
    transferOrderDetails && Object.keys(transferOrderDetails).length > 0;
  
  const transferedL0Details = isOrderTransfer
  ? {
      attributedTo: transferOrderDetails?.l0Info?.uamId,
      attributedToName: transferOrderDetails?.l0Info?.email,
    }
  : {};

  const stepsLabels = isOrderTransfer
    ? [...STEPS_LABEL, { label: "Review Transfer Order" }]
    : STEPS_LABEL;

  const isInvalidParam =
    !uam_id ||
    !selectedBusinessVerticalId ||
    uam_id === "null" ||
    uam_id === "undefined" ||
    selectedBusinessVerticalId === "null" ||
    selectedBusinessVerticalId === "undefined";

  const isMissingCustomerCountry =
    customerCountryIdParam == null ||
    customerCountryIdParam === "" ||
    customerCountryIdParam === "null" ||
    customerCountryIdParam === "undefined";

  const {
    mutate: punchOrder,
    isPending: isPunchOrderLoading,
    isError,
    error,
  } = usePunchOrder();

  const {
    isPending: isSelectedSelectedUserDetailsLoading,
    data: selectedUserDetails,
    isError: isErrorSelectedSelectedUserDetails,
    error: errorMessageOnSelectedSelectedUserDetails,
  } = useFetchUser(uam_id, selectedBusinessVerticalId);

  const transferOrderIdForQuery =
    isOrderTransfer && transferOrderDetails?.orderId
      ? transferOrderDetails.orderId
      : null;

  const { data: orderInfoForTransfer } = useSelectedOrderDetails(
    transferOrderIdForQuery,
    { countryId: customerCountryIdParam },
  );

  const customerCountryId = customerCountryIdParam
    ? Number(customerCountryIdParam)
    : null;

  const { data: countryIsdList = [] } = useCountryIsdList();
  const { data: currencyList = [] } = useCurrencyList();
  const { data: currencyByCountryMap = {} } = useCurrencyByCountryMap();
  const customerCountry = findCountryInIsdList(countryIsdList, customerCountryId);

  const transferOrderDetailsForL0 = useMemo(() => {
    if (!isOrderTransfer || !transferOrderDetails) {
      return null;
    }
    if (orderInfoForTransfer) {
      return {
        ...transferOrderDetails,
        ...camelizeKeys(orderInfoForTransfer),
      };
    }
    return transferOrderDetails;
  }, [isOrderTransfer, transferOrderDetails, orderInfoForTransfer]);

  const previousOrderHadShipping = orderHadShippingAddress(
    transferOrderDetailsForL0 || transferOrderDetails
  );

  if (isInvalidParam) {
    return <PageNotFound />;
  }

  if (isMissingCustomerCountry) {
    return (
      <ErrorFallback message="Customer country is required. Please search the customer again so a country is attached." />
    );
  }

  if (isSelectedSelectedUserDetailsLoading) {
    return <FullPageLoader />;
  }

  if (isErrorSelectedSelectedUserDetails) {
    return (
      <ErrorFallback
        message={errorMessageOnSelectedSelectedUserDetails.message}
      />
    );
  }

  const transferedPackageSubscriptions = isOrderTransfer
    ? getPackageSubscriptions(
        transferOrderDetails?.packages,
        transferOrderDetails?.businessUnitId
      )
    : [];

  const previousOrderAcademicYear = isOrderTransfer
    ? getPreviousOrderAcademicYear(transferOrderDetails)
    : null;

  const transferedOrderVerticalAndTenantIds = isOrderTransfer
    ? getVerticalAndTenantIdsOfTransferedOrder(transferOrderDetails)
    : {
        verticalId: null,
        tenantId: null,
        officeLocationId: null,
      };

  const initialValues = getInitialValues(
    transferedPackageSubscriptions,
    transferedOrderVerticalAndTenantIds,
    selectedBusinessVerticalId,
    isOrderTransfer ? transferOrderDetailsForL0 : null,
    customerCountryId,
  );

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={true}
      validateOnBlur={true}
      validate={(values) => {
        const businessUnitId = values.filters?.selectedVerticalId;
        const tenantStateId = values.filters?.selectedOfficeLocationId;
        const hasHardware = Boolean(values.orderDetails?.hasHardware);
        const useSameAddress = Boolean(values.useSameAddress);

        const selectedAddressCountry = findCountryInIsdList(
          countryIsdList,
          values.filters?.selectedCountryId
        );

        const { errors: addressErrors } = validateOrderAddresses(
          values.orderDetails,
          {
            isdCode: selectedAddressCountry?.isd_code || "",
            countryCode: selectedAddressCountry?.country_code || "",
            businessUnitId,
            tenantStateId,
            hasHardware,
            useSameAddress,
          }
        );

        return Object.keys(addressErrors).length > 0
          ? { orderDetails: addressErrors }
          : undefined;
      }}
    >
      {(formikProps) => {
        const {
          values,
          errors,
          setFieldValue,
          isValid,
          isSubmitting,
          handleBlur,
          validateForm,
        } = formikProps;

        const {
          orderDetails,
          currentStep,
          selectedPackageSubscriptions,
          filters,
          searchTerm,
          useSameAddress,
          openDialog,
          priceDetailsAfterDiscount,
        } = values;

        const selectedAddressCountry =
          findCountryInIsdList(countryIsdList, filters.selectedCountryId) ||
          customerCountry;
        const selectedIsdCode = selectedAddressCountry?.isd_code ?? "";
        const selectedCountryCode = selectedAddressCountry?.country_code ?? "";
        const selectedCountryDisplayName =
          selectedAddressCountry?.display_name ?? "";
        const selectedCountryApiName =
          selectedAddressCountry?.country_name ?? "";

        const currencyCode = resolveCurrencyCodeByCountryId(
          filters.selectedCountryId,
          currencyByCountryMap,
          currencyList,
        );

        const modifiedOrderAmount =
          priceDetailsAfterDiscount?.orderAmount !== undefined &&
          priceDetailsAfterDiscount?.orderAmount !== null
            ? priceDetailsAfterDiscount.orderAmount
            : totalMrp(selectedPackageSubscriptions);

        return (
          <div className={styles.root}>
            <SyncTransferOrderL0Fields
              isOrderTransfer={isOrderTransfer}
              orderInfoForTransfer={orderInfoForTransfer}
              transferOrderDetails={transferOrderDetails}
              setFieldValue={setFieldValue}
            />
            <div className={styles.stepperCard}>
              <Stepper activeStep={currentStep}>
                <Stepper.Steps steps={stepsLabels} activeStep={currentStep} />
              </Stepper>
            </div>
            <div className={styles.stepContent}>
              {currentStep === PACKAGE_SUBSCRIPTION_SELECTION ? (
                <PackageSubscription
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  filters={filters}
                  searchTerm={searchTerm}
                  isOrderTransfer={isOrderTransfer}
                  customerCountryId={customerCountryId}
                  previousOrderAcademicYear={previousOrderAcademicYear}
                  onPackagesSelected={(packages) =>
                    setFieldValue("selectedPackageSubscriptions", packages)
                  }
                  onFilterChange={(filterType, value) => {
                    setFieldValue(`filters.${filterType}`, value);
                  }}
                  onSearchTermChange={(value) => {
                    setFieldValue("searchTerm", value);
                  }}
                  handleChangePackageSubscriptionSelection={(packages) =>
                    setFieldValue("selectedPackageSubscriptions", packages)
                  }
                  currencyCode={currencyCode}
                />
              ) : null}
              {currentStep === ADDRESS_FORM_AND_DISCOUNT_APPLICATION ? (
                <OrderSummary
                  orderDetails={orderDetails}
                  selectedVerticalId={filters.selectedVerticalId}
                  gatewayTenantId={filters.selectedGatewayTenantId}
                  countryName={selectedCountryDisplayName}
                  isdCode={selectedIsdCode}
                  countryCode={selectedCountryCode}
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  setFieldValue={setFieldValue}
                  useSameAddress={useSameAddress}
                  isOrderTransfer={isOrderTransfer}
                  errors={errors}
                  handleBlur={handleBlur}
                  validateForm={validateForm}
                  currencyCode={currencyCode}
                />
              ) : null}
              {isOrderTransfer && currentStep === REVIEW_TRANSFER_ORDER && (
                <ReviewTransferOrder
                  transferOrderDetails={transferOrderDetails}
                  billingAddress={orderDetails.billingAddress}
                  shippingAddress={orderDetails.shippingAddress}
                  useSameAddress={useSameAddress}
                  hasHardware={orderDetails.hasHardware}
                  selectedPackageSubscriptions={selectedPackageSubscriptions}
                  discountDetails={orderDetails.discount}
                  newOrderAmount={modifiedOrderAmount}
                  currencyCode={currencyCode}
                  isdCode={selectedIsdCode}
                  countryName={selectedCountryApiName}
                />
              )}
            </div>
            <PunchOrderStepActions
              currentStep={currentStep}
              setFieldValue={setFieldValue}
              selectedPackageSubscriptions={selectedPackageSubscriptions}
              filters={filters}
              isValid={isValid}
              isOrderTransfer={isOrderTransfer}
              isSubmitting={isSubmitting}
              orderDetails={orderDetails}
              openSnackbar={openSnackbar}
              values={values}
              navigate={navigate}
              punchOrder={punchOrder}
              sales_person_id={sales_person_id}
              uam_id={uam_id}
              isPunchOrderLoading={isPunchOrderLoading}
              error={error}
              isError={isError}
              transferOrderDetails={transferOrderDetailsForL0 || transferOrderDetails}
              selectedBusinessVerticalId={selectedBusinessVerticalId}
              customerCountryId={customerCountryId}
              customerIsdCode={selectedIsdCode}
              customerCountryCode={selectedCountryCode}
              customerCountryName={selectedCountryApiName}
              previousOrderHadShipping={previousOrderHadShipping}
            />
            <Dialog
              open={!!openDialog}
              handleClose={() => setFieldValue("openDialog", false)}
              width={openDialog?.size?.width}
              height={openDialog?.size?.height}
            >
              {openDialog?.content}
            </Dialog>
          </div>
        );
      }}
    </Formik>
  );
};

export default PunchOrder;
